import { useMemo, useState } from 'react'
import type { Profile, WeightEntry } from '../../lib/metrics'
import { targetCurve } from '../../lib/metrics'
import { fmt, trDate } from '../../lib/format'

const W = 760, H = 300, L = 52, R = 58, T = 16, B = 34
const PX = W - L - R, PY = H - T - B
const DAY = 86_400_000

interface Props { profile: Profile; weights: WeightEntry[] }

export function WeightChart({ profile, weights }: Props) {
  const [hover, setHover] = useState<{ x: number; y: number; date: string; kg: number } | null>(null)

  const view = useMemo(() => {
    const t0 = weights.length ? new Date(weights[0].date).getTime() : Date.now()
    const tLast = weights.length ? new Date(weights[weights.length - 1].date).getTime() : t0
    const x1 = Math.max(tLast + 21 * DAY, t0 + 84 * DAY)

    const target = targetCurve(profile, t0, x1)
    const values = [...weights.map((w) => w.kg), ...target.map((p) => p.kg), profile.startWeightKg]
    let lo = Math.min(...values), hi = Math.max(...values)
    const pad = Math.max(1.5, (hi - lo) * 0.14)
    lo = Math.floor((lo - pad) / 2) * 2
    hi = Math.ceil((hi + pad) / 2) * 2

    const X = (ms: number) => L + ((ms - t0) / (x1 - t0)) * PX
    const Y = (kg: number) => T + ((hi - kg) / (hi - lo)) * PY

    return { t0, x1, target, lo, hi, X, Y }
  }, [profile, weights])

  const { t0, x1, target, lo, hi, X, Y } = view
  const path = (pts: { ms: number; kg: number }[]) =>
    pts.map((p, i) => `${i ? 'L' : 'M'} ${X(p.ms).toFixed(1)} ${Y(p.kg).toFixed(1)}`).join(' ')

  const actual = weights.map((w) => ({ ms: new Date(w.date).getTime(), kg: w.kg, date: w.date }))
  const step = Math.max(2, Math.round((hi - lo) / 6 / 2) * 2)
  const yTicks: number[] = []
  for (let v = lo; v <= hi; v += step) yTicks.push(v)
  const xTicks: number[] = []
  for (let d = t0; d <= x1; d += 28 * DAY) xTicks.push(d)

  const last = actual[actual.length - 1]
  const tEnd = target[target.length - 1]

  return (
    <div style={{ position: 'relative' }}>
      <div className="legend">
        <span className="k"><i className="swatch" style={{ background: 'var(--s1)' }} />Gerçek kilo</span>
        <span className="k"><i className="swatch" style={{ background: 'var(--s2)' }} />Hedef eğrisi</span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Kilo seyri grafiği">
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={L} y1={Y(v)} x2={W - R} y2={Y(v)} style={{ stroke: 'var(--grid)' }} strokeWidth={1} />
            <text x={L - 11} y={Y(v) + 4} textAnchor="end" fontSize={11.5}
                  style={{ fill: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{v}</text>
          </g>
        ))}

        {xTicks.map((d) => (
          <g key={`x${d}`}>
            <line x1={X(d)} y1={T} x2={X(d)} y2={H - B} style={{ stroke: 'var(--grid)' }} strokeWidth={1} />
            <text x={X(d)} y={H - B + 19} textAnchor="middle" fontSize={11.5}
                  style={{ fill: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
              {new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
            </text>
          </g>
        ))}

        <line x1={L} y1={H - B} x2={W - R} y2={H - B} style={{ stroke: 'var(--axis)' }} strokeWidth={1} />

        <path d={path(target)} fill="none" style={{ stroke: 'var(--s2)' }}
              strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {tEnd && (
          <text x={W - R + 9} y={Y(tEnd.kg) + 4} fontSize={11.5} style={{ fill: 'var(--muted)' }}>hedef</text>
        )}

        {actual.length > 1 && (
          <path d={path(actual)} fill="none" style={{ stroke: 'var(--s1)' }}
                strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        )}

        {actual.map((p, i) => (
          <circle key={p.date} cx={X(p.ms)} cy={Y(p.kg)} r={i === actual.length - 1 ? 5 : 3.5}
                  style={{ fill: 'var(--s1)', stroke: 'var(--surface)' }} strokeWidth={2} />
        ))}

        {last && (
          <text x={X(last.ms) + 11} y={Y(last.kg) - 9} fontSize={12.5} fontWeight={600}
                style={{ fill: 'var(--ink)' }}>{fmt(last.kg)} kg</text>
        )}

        {/* Hit alanı marktan büyük — 8px'lik bir noktaya nişan almak zorunda kalma */}
        {actual.map((p) => (
          <circle key={`hit-${p.date}`} cx={X(p.ms)} cy={Y(p.kg)} r={14} fill="transparent" tabIndex={0}
                  onMouseEnter={() => setHover({ x: X(p.ms), y: Y(p.kg), date: p.date, kg: p.kg })}
                  onFocus={() => setHover({ x: X(p.ms), y: Y(p.kg), date: p.date, kg: p.kg })}
                  onMouseLeave={() => setHover(null)}
                  onBlur={() => setHover(null)} />
        ))}

        {!actual.length && (
          <text x={L + PX * 0.62} y={T + 28} textAnchor="middle" fontSize={13} style={{ fill: 'var(--muted)' }}>
            İlk tartını ekleyince mavi çizgi burada belirecek
          </text>
        )}
      </svg>

      {hover && (
        <div className="tip" style={{ left: `${(hover.x / W) * 100}%`, top: `${(hover.y / H) * 100}%` }}>
          <strong>{fmt(hover.kg)} kg</strong>
          <br />
          <span style={{ color: 'var(--muted)' }}>{trDate(hover.date)}</span>
        </div>
      )}
    </div>
  )
}
